import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import ScensobConnectLogo from '../components/brand/ScensobConnectLogo';
import { apiCall, downloadFile } from '../utils/api';
import { getCurrentLocation, getLocationPermissionState, disableLocation } from '../utils/location';
import { toast } from '../components/ui/sonner';
import { 
  MapPin, DollarSign, Clock, Briefcase, Wallet, User, MessageSquare, 
  LogOut, Search, Filter, ChevronRight, Calendar, TrendingUp,
  CheckCircle, AlertCircle, RefreshCw, Star, Award, Zap, Play, Square, Settings,
  Bookmark, FileText, PieChart, Landmark, X, UserPlus, Send, XCircle, ChevronDown, HelpCircle,
  Bell, Shield, Globe, Moon, Sun, Mail, Phone, ExternalLink, MessageCircle,
  Home, Rss, Megaphone, GraduationCap, ArrowLeftRight, BookOpen, Trophy, Gift, Heart,
  Sparkles, Flame, Navigation, Banknote, BarChart3, UserPlus as UserPlusIcon,
  Store, History, Users2, Clock as ClockIcon, Camera, Download, AlertTriangle, Edit3
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import RoleSwitcher from '../components/RoleSwitcher';
import { PayRate } from '../components/shared/PayLabel';
import useUnreadMessages from '../hooks/useUnreadMessages';
// Header Messages icon now routes to the 'messages' tab which renders
// WorkerInbox; the legacy MessagingInbox/RealTimeChat modal pair is no
// longer wired in this dashboard.
import WorkerProfileSettings from '../components/WorkerProfileSettings';
import WorkerHrisPage from '../components/hris/WorkerHrisPage';
import SensitiveGate from '../components/security/SensitiveGate';
import CertificateManagement from '../components/CertificateManagement';
import WalletManagement from '../components/WalletManagement';
import EarningsDashboard from '../components/EarningsDashboard';
import JobCenter from '../components/JobCenter';
import LiveChatSupport from '../components/LiveChatSupport';
import JobAlerts from '../components/JobAlerts';
import AvailabilityManager from '../components/AvailabilityManager';
import ReportButton from '../components/ReportButton';
import ShiftGroupChat from '../components/ShiftGroupChat';
// Dashboard cleanup (Jul 2026): tab panels are code-split with React.lazy
// so the initial worker bundle stays lean. Each loads on first tab visit.
const CommunityBoard = lazy(() => import('../components/community/CommunityBoard'));
const FinancialSummaryCard = lazy(() => import('../components/wallet/FinancialSummaryCard'));
const FinancialTimeline = lazy(() => import('../components/wallet/FinancialTimeline'));
const DistanceUnitToggle = lazy(() => import('../components/org/DistanceUnitToggle'));
const TransactionsList = lazy(() => import('../components/wallet/TransactionsList'));
const StatementDownload = lazy(() => import('../components/wallet/StatementDownload'));
const NotificationPrefsCard = lazy(() => import('../components/shared/NotificationPrefsCard'));
const HelpTraining = lazy(() => import('../components/shared/HelpTraining'));
const OnboardingCard = lazy(() => import('../components/shared/OnboardingCard'));
const GPSCheckInOut = lazy(() => import('../components/GPSCheckInOut'));
const LocationHelpModal = lazy(() => import('../components/shared/LocationHelpModal'));
const TimeEditRequestModal = lazy(() => import('../components/gps/TimeEditRequestModal'));
const AdvancedJobFilters = lazy(() => import('../components/AdvancedJobFilters'));
const WorkerTimesheets = lazy(() => import('../components/WorkerTimesheets'));
const WorkerPayouts = lazy(() => import('../components/WorkerPayouts'));
const DashboardHome = lazy(() => import('../components/DashboardHome'));
const EarningsForecaster = lazy(() => import('../components/EarningsForecaster'));
const InstantPay = lazy(() => import('../components/InstantPay'));
const ReferralSystem = lazy(() => import('../components/ReferralSystem'));
const AIRecommendations = lazy(() => import('../components/AIRecommendations'));
const CommutePlanner = lazy(() => import('../components/CommutePlanner'));
const AvailabilityHeatmap = lazy(() => import('../components/AvailabilityHeatmap'));
const SurgeShifts = lazy(() => import('../components/SurgeShifts'));
const WellnessDashboard = lazy(() => import('../components/WellnessDashboard'));
const WorkerDirectory = lazy(() => import('../components/WorkerDirectory'));
const ShiftHistory = lazy(() => import('../components/ShiftHistory'));
const Marketplace = lazy(() => import('../components/Marketplace'));
const AvailabilityCalendarPro = lazy(() => import('../components/AvailabilityCalendarPro'));
const WorkerFeed = lazy(() => import('../components/WorkerFeed'));
const HRISWorkerTraining = lazy(() => import('../components/hris/HRISWorkerTraining'));
const TrainingStreakBadge = lazy(() => import('../components/hris/TrainingStreakBadge'));
const ShiftSwapMarketplace = lazy(() => import('../components/ShiftSwapMarketplace'));
const ShiftJournal = lazy(() => import('../components/ShiftJournal'));
const WorkerLeaderboard = lazy(() => import('../components/WorkerLeaderboard'));
const WeeklyChallenges = lazy(() => import('../components/WeeklyChallenges'));
const DailySpinWheel = lazy(() => import('../components/DailySpinWheel'));
const ShiftCalendar = lazy(() => import('../components/ShiftCalendar'));
const WorkerInbox = lazy(() => import('../components/WorkerInbox'));
const JobInvites = lazy(() => import('../components/JobInvites'));
const WorkerPaymentClaims = lazy(() => import('../components/worker/WorkerPaymentClaims'));
const ReliabilityBadge = lazy(() => import('../components/worker/ReliabilityBadge'));
const StreakRewardBanner = lazy(() => import('../components/worker/StreakRewardBanner'));
import WorkerRejectModal from '../components/worker/WorkerRejectModal';
import WorkerRatingModal from '../components/worker/WorkerRatingModal';
import WorkerProfileTab from '../components/worker/WorkerProfileTab';
import WorkerWalletTab from '../components/worker/WorkerWalletTab';
import WorkerMyShiftsTab from '../components/worker/WorkerMyShiftsTab';
import WorkerJobsTab from '../components/worker/WorkerJobsTab';

export default function WorkerDashboard() {
  const { user, logout, updateUser } = useAuth();
  const { isConnected } = useWebSocket();
  const [activeTab, setActiveTab] = useState('home');
  const [openNavGroup, setOpenNavGroup] = useState(null);
  const navRef = useRef(null);

  // Phase A: load persisted distance unit preference
  useEffect(() => {
    apiCall('/api/users/me/distance-unit').then((d) => setDistanceUnit(d.unit)).catch(() => {});
  }, []);

  // Close any open nav-group dropdown when the user clicks outside the nav
  useEffect(() => {
    if (!openNavGroup) return undefined;
    const onDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenNavGroup(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openNavGroup]);
  const { unread: unreadMessages } = useUnreadMessages();
  const [jobs, setJobs] = useState([]);
  const [distanceUnit, setDistanceUnit] = useState('km');
  const [myShifts, setMyShifts] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [walletTxns, setWalletTxns] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [location, setLocation] = useState(null);
  const [jobsMeta, setJobsMeta] = useState({ locationRequired: false, effectiveRadiusKm: null });
  const [showLocationHelp, setShowLocationHelp] = useState(false);
  const [loading, setLoading] = useState(false);
  // Header Messages icon now routes to the 'messages' tab (WorkerInbox) for
  // parity with the taskbar. The modal-based MessagingInbox/RealTimeChat flow
  // has been removed.
  const [showFilters, setShowFilters] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showCertificates, setShowCertificates] = useState(false);
  const [showWalletManagement, setShowWalletManagement] = useState(false);
  const [showEarnings, setShowEarnings] = useState(false);
  const [showJobCenter, setShowJobCenter] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(null);
  const [showSwitchModal, setShowSwitchModal] = useState(null);
  // iter188: page-level edit-shift modal for the completed-card layout.
  // `editShiftTarget` = the shift being edited; the modal composes a
  // TimeEditRequestCreate payload and POSTs to /api/worker/time-edit-requests.
  const [editShiftTarget, setEditShiftTarget] = useState(null);
  const [editShiftForm, setEditShiftForm] = useState({
    requested_checkin_time: '',
    requested_checkout_time: '',
    reason: '',
    shift_notes: '',
  });
  const [editShiftLoading, setEditShiftLoading] = useState(false);
  const [switchableShifts, setSwitchableShifts] = useState([]);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [selectedTransferWorker, setSelectedTransferWorker] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [showJobAlerts, setShowJobAlerts] = useState(false);
  const [shiftFilter, setShiftFilter] = useState('all');
  const [showRatingModal, setShowRatingModal] = useState(null);
  const [showGroupChat, setShowGroupChat] = useState(null);
  const [ratingData, setRatingData] = useState({ management: 0, work_environment: 0, resources_available: 0, communication: 0, review: '', would_work_again: null, anonymous: false });
  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratedJobs, setRatedJobs] = useState({});
  const jobAlertsRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const profilePhotoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    industry: '',
    pay_min: '',
    pay_max: '',
    radius_km: 50,
    start_date: '',
    end_date: '',
    skills: '',
    sort_by: 'date',
  });
  const [platformMinPay, setPlatformMinPay] = useState(null);
  const [workerMinRate, setWorkerMinRate] = useState(null);
  const [hideBelowMin, setHideBelowMin] = useState(false);
  const [lowPayConfirmJob, setLowPayConfirmJob] = useState(null);

  // Helper for profile photo URL
  const getProfilePhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    if (photo.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
    if (photo.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}/api${photo}`;
    return photo;
  };

  const handleProfileUpdate = (updatedUser) => {
    // Refresh the page to get updated user info
    window.location.reload();
  };

  // Upload a new profile photo from the dropdown camera badge.
  // Uses the existing /api/worker/profile/photo endpoint and refreshes the
  // in-memory user via updateUser() so the avatar updates instantly without
  // a hard reload.
  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose a JPEG, PNG, or WebP image');
      e.target.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image too large — max 2 MB');
      e.target.value = '';
      return;
    }
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/worker/profile/photo`, {
        method: 'POST',
        // iter77 Phase B: cookie-based auth, no Bearer header needed.
        credentials: 'include',
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Upload failed');
      updateUser({ ...user, profile_photo: data.file_url, profile_image: data.file_url });
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.message || 'Could not upload photo');
    } finally {
      setUploadingPhoto(false);
      if (e.target) e.target.value = '';
    }
  };

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (jobAlertsRef.current && !jobAlertsRef.current.contains(event.target)) {
        setShowJobAlerts(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadLocation();
    loadWallet();
    loadEarnings();
    loadMinPayPrefs();
  }, []);

  useEffect(() => {
    if (activeTab === 'jobs') {
      loadJobs();
    } else if (activeTab === 'my-shifts') {
      loadMyShifts();
    }
  }, [location, filters, activeTab]);

  // Live sync: refresh the active view + money widgets when data changes anywhere.
  useLiveRefresh(['jobs', 'shifts', 'applications', 'swaps', 'transfers', 'attendance', 'payments', 'payroll', 'wallet'], () => {
    if (activeTab === 'jobs') loadJobs();
    else if (activeTab === 'my-shifts') loadMyShifts();
    loadWallet();
    loadEarnings();
  });

  // Toast guards — UAT flagged "duplicate location alerts" from repeated calls.
  // We remember the last toast variant we surfaced and only show a new one when
  // the state actually changes.
  const lastLocationToastRef = React.useRef(null);
  const locationDeniedRef = React.useRef(false);

  const loadLocation = async (opts = {}) => {
    // If the user previously denied permission in this session, don't
    // re-prompt on every mount — respect their choice until they click
    // the explicit "Enable Location" button (which passes forceFresh).
    if (locationDeniedRef.current && !opts.forceFresh) {
      return;
    }
    try {
      const state = await getLocationPermissionState();
      // iter181 — Explicit click on "Enable Location":
      //   • state 'granted' or 'prompt' → getCurrentPosition triggers the
      //     native prompt (or resolves immediately). Works as expected.
      //   • state 'denied' → no JS API can force a re-prompt. We open a
      //     browser-specific help modal so the user can flip it back in
      //     one click via the address-bar padlock.
      if (state === 'denied' && opts.forceFresh) {
        setShowLocationHelp(true);
        locationDeniedRef.current = true;
        return;
      }
      if (state === 'denied' && !opts.forceFresh) {
        locationDeniedRef.current = true;
        if (lastLocationToastRef.current !== 'denied') {
          toast.info('Location is disabled in your browser — showing all jobs. Tap "Enable Location" for help.');
          lastLocationToastRef.current = 'denied';
        }
        return;
      }
      const pos = await getCurrentLocation({ forceFresh: !!opts.forceFresh, silent: !opts.forceFresh });
      if (pos) {
        setLocation(pos);
        locationDeniedRef.current = false;
        if (lastLocationToastRef.current !== 'success') {
          toast.success('Location enabled');
          lastLocationToastRef.current = 'success';
        }
      }
    } catch (error) {
      const code = error?.code;
      if (code === 'PERMISSION_DENIED') {
        locationDeniedRef.current = true;
        // Explicit click failed because browser blocked → guide the user.
        if (opts.forceFresh) {
          setShowLocationHelp(true);
          return;
        }
      }
      if (lastLocationToastRef.current !== code) {
        const friendly = {
          PERMISSION_DENIED: 'Location permission denied. Showing all jobs.',
          POSITION_UNAVAILABLE: 'Your device could not determine its position. Showing all jobs.',
          TIMEOUT: 'Location lookup timed out. Showing all jobs.',
          UNSUPPORTED: 'This browser does not support location. Showing all jobs.',
        };
        if (opts.forceFresh) toast.error(friendly[code] || 'Could not enable location.');
        lastLocationToastRef.current = code || 'error';
      }
      if (process.env.NODE_ENV !== 'production') console.debug('Location not available:', code);
    }
  };

  // Manual "Disable Location" — flips the app-layer state back off. The
  // browser permission itself cannot be revoked from JS in any modern
  // browser, so we clear the session cache + our state so nothing else
  // sends coordinates until the user re-clicks Enable.
  const handleDisableLocation = () => {
    disableLocation();
    setLocation(null);
    locationDeniedRef.current = false;
    lastLocationToastRef.current = null;
    toast.success('Location disabled for this session');
  };

  const loadMyShifts = async () => {
    setLoading(true);
    try {
      // include_closed=true so the "Completed" tile counts + lists paid-out
      // shifts. The "in_progress" / "upcoming" filters still exclude them
      // client-side via SHIFT_FILTERS.
      const response = await apiCall('/api/worker/assigned-shifts?include_closed=true');
      setMyShifts(response.shifts || []);
      loadPendingTransfers();
    } catch (error) {
      console.error('Failed to load shifts:', error);
      setMyShifts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingTransfers = async () => {
    try {
      const response = await apiCall('/api/worker/pending-transfers');
      setPendingTransfers(response.transfers || []);
    } catch (error) {
      console.error('Failed to load transfers:', error);
    }
  };

  const loadAvailableWorkers = async (jobId = null) => {
    try {
      const qs = jobId ? `?job_id=${encodeURIComponent(jobId)}` : '';
      const response = await apiCall(`/api/worker/available-workers${qs}`);
      setAvailableWorkers(response.workers || []);
    } catch (error) {
      console.error('Failed to load available workers:', error);
    }
  };

  const loadSwitchableShifts = async (jobId) => {
    setSwitchLoading(true);
    try {
      const res = await apiCall(`/api/worker/switchable-shifts/${jobId}`);
      setSwitchableShifts(res.shifts || []);
    } catch (error) {
      setSwitchableShifts([]);
    } finally {
      setSwitchLoading(false);
    }
  };

  const openEditShiftModal = (shift) => {
    setEditShiftTarget(shift);
    setEditShiftForm({
      requested_checkin_time: '',
      requested_checkout_time: '',
      reason: '',
      shift_notes: '',
    });
  };

  const handleEditShiftFormChange = (key, val) => {
    setEditShiftForm(f => ({ ...f, [key]: val }));
  };

  const handleSubmitEditShift = async () => {
    if (!editShiftTarget) return;
    const es = editShiftTarget.edit_status;
    if (!es?.attendance_id) { toast.error('Edit unavailable — no attendance record found'); return; }
    const hasTimeChange = Boolean(editShiftForm.requested_checkin_time || editShiftForm.requested_checkout_time);
    const hasNotes = Boolean(editShiftForm.shift_notes?.trim());
    if (!hasTimeChange && !hasNotes) {
      toast.error('Add shift notes or specify at least one time to change');
      return;
    }
    if (hasTimeChange && (!editShiftForm.reason || editShiftForm.reason.length < 10)) {
      toast.error('Please provide a reason (min 10 characters) when changing times');
      return;
    }
    setEditShiftLoading(true);
    try {
      const payload = {
        attendance_id: es.attendance_id,
        job_id: editShiftTarget._id,
        reason: editShiftForm.reason || '',
      };
      if (editShiftForm.shift_notes?.trim()) payload.shift_notes = editShiftForm.shift_notes.trim();
      if (editShiftForm.requested_checkin_time) payload.requested_checkin_time = new Date(editShiftForm.requested_checkin_time).toISOString();
      if (editShiftForm.requested_checkout_time) payload.requested_checkout_time = new Date(editShiftForm.requested_checkout_time).toISOString();
      await apiCall('/api/worker/time-edit-requests', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      toast.success(hasTimeChange ? 'Edit request submitted — HR / admin will review it.' : 'Shift notes saved.');
      setEditShiftTarget(null);
      loadMyShifts();
    } catch (e) {
      toast.error(e.message || 'Failed to submit edit');
    } finally {
      setEditShiftLoading(false);
    }
  };

  const handleConfirmSwitch = async (toJobId) => {
    if (!showSwitchModal) return;
    setSwitchLoading(true);
    try {
      const body = { from_job_id: showSwitchModal._id, to_job_id: toJobId };
      // Best-effort location — server accepts null values
      try {
        const pos = await getCurrentLocation({ silent: true });
        if (pos) { body.lat = pos.lat; body.lng = pos.lng; }
      } catch (_e) { /* location not required */ }
      const res = await apiCall('/api/attendance/switch', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      toast.success(`Switched — logged ${res.closed_duration_hours}h on previous shift`);
      setShowSwitchModal(null);
      setSwitchableShifts([]);
      loadMyShifts();
    } catch (error) {
      toast.error(error.message || 'Failed to switch shift');
    } finally {
      setSwitchLoading(false);
    }
  };

  const handleSubmitRating = async () => {
    if (!showRatingModal) return;
    const { management, work_environment, resources_available, communication } = ratingData;
    if (!management || !work_environment || !resources_available || !communication) {
      toast.error('Please rate all categories'); return;
    }
    setRatingLoading(true);
    try {
      await apiCall(`/api/worker/jobs/${showRatingModal._id}/rate`, {
        method: 'POST',
        body: JSON.stringify(ratingData)
      });
      toast.success('Rating submitted!');
      setRatedJobs(prev => ({ ...prev, [showRatingModal._id]: true }));
      setShowRatingModal(null);
      setRatingData({ management: 0, work_environment: 0, resources_available: 0, communication: 0, review: '', would_work_again: null, anonymous: false });
    } catch (error) {
      toast.error(error.message || 'Failed to submit rating');
    } finally { setRatingLoading(false); }
  };

  const openRatingModal = async (shift) => {
    try {
      const res = await apiCall(`/api/worker/jobs/${shift._id}/rating`);
      if (res.rating) {
        toast.info('You have already rated this shift');
        setRatedJobs(prev => ({ ...prev, [shift._id]: true }));
        return;
      }
    } catch (e) {
      // Pre-existing rating check is best-effort — keep the modal open if it fails.
      if (process.env.NODE_ENV !== 'production') console.debug('rating lookup failed:', e?.message);
    }
    setRatingData({ management: 0, work_environment: 0, resources_available: 0, communication: 0, review: '', would_work_again: null, anonymous: false });
    setShowRatingModal(shift);
  };

  // iter180: "In Progress" strictly means the worker clocked in. "Upcoming"
  // is anything before check-in that isn't yet closed. "Completed" covers
  // the whole post-checkout tail: waiting employer approval, approved, and
  // paid — so the counter matches the user's mental model.
  const SHIFT_FILTERS = {
    in_progress: (s) => s.status === 'in_progress',
    upcoming: (s) => ['assigned', 'confirmed', 'active'].includes(s.status),
    completed: (s) => ['completed', 'pending_approval', 'approved', 'paid'].includes(s.status),
  };
  const filteredShifts = SHIFT_FILTERS[shiftFilter]
    ? myShifts.filter(SHIFT_FILTERS[shiftFilter])
    : myShifts;
  const inProgressShifts = myShifts.filter(SHIFT_FILTERS.in_progress);
  const upcomingCount = myShifts.filter(SHIFT_FILTERS.upcoming).length;
  const completedCount = myShifts.filter(SHIFT_FILTERS.completed).length;

  const handleConfirmShift = async (jobId) => {
    setActionLoading(true);
    try {
      await apiCall(`/api/worker/shifts/${jobId}/confirm`, { method: 'POST' });
      toast.success('Shift confirmed!');
      loadMyShifts();
    } catch (error) {
      toast.error(error.message || 'Failed to confirm shift');
    } finally { setActionLoading(false); }
  };

  const handleRejectConfirmation = async (jobId, reason = '') => {
    setActionLoading(true);
    try {
      await apiCall(`/api/worker/shifts/${jobId}/reject-confirmation`, {
        method: 'POST', body: JSON.stringify({ reason })
      });
      toast.success('Shift declined');
      loadMyShifts();
    } catch (error) {
      toast.error(error.message || 'Failed to decline shift');
    } finally { setActionLoading(false); }
  };

  const [alertSetShifts, setAlertSetShifts] = useState({});

  // Load which shifts already have alerts set (persisted in backend)
  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall('/api/worker/job-alerts/set-jobs');
        const matched = {};
        (res.job_ids || []).forEach(id => { matched[id] = true; });
        setAlertSetShifts(matched);
      } catch (e) {
        // Non-critical — falls back to empty alertSetShifts state.
        if (process.env.NODE_ENV !== 'production') console.debug('job-alerts/set-jobs load failed:', e?.message);
      }
    })();
  }, []);

  const handleSetJobAlert = async (shift) => {
    try {
      const res = await apiCall('/api/worker/job-alerts/from-shift', {
        method: 'POST',
        body: JSON.stringify({
          job_id: shift._id,
          title: shift.title,
          industry: shift.industry || '',
          category: shift.category || '',
          required_skills: shift.required_skills || [],
          pay_rate: shift.pay_rate || 0,
        })
      });
      setAlertSetShifts(prev => ({ ...prev, [shift._id]: true }));
      toast.success(res.message || 'Job alert set!');
    } catch (error) {
      toast.error(error.message || 'Failed to set alert');
    }
  };

  const handleRejectShift = async () => {
    if (!showRejectModal) return;
    setActionLoading(true);
    try {
      await apiCall(`/api/worker/shifts/${showRejectModal._id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: rejectReason })
      });
      toast.success('Shift rejected successfully');
      setShowRejectModal(null);
      setRejectReason('');
      loadMyShifts();
    } catch (error) {
      toast.error(error.message || 'Failed to reject shift');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransferShift = async () => {
    if (!showTransferModal || !selectedTransferWorker) return;
    setActionLoading(true);
    try {
      await apiCall(`/api/worker/shifts/${showTransferModal._id}/transfer`, {
        method: 'POST',
        body: JSON.stringify({ 
          target_worker_id: selectedTransferWorker._id,
          reason: transferReason 
        })
      });
      toast.success(`Transfer request sent to ${selectedTransferWorker.full_name}`);
      setShowTransferModal(null);
      setSelectedTransferWorker(null);
      setTransferReason('');
    } catch (error) {
      toast.error(error.message || 'Failed to request transfer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRespondToTransfer = async (transferId, action) => {
    setActionLoading(true);
    try {
      await apiCall(`/api/worker/transfer/${transferId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ action })
      });
      toast.success(action === 'accept' ? 'Transfer accepted!' : 'Transfer declined');
      loadPendingTransfers();
      loadMyShifts();
    } catch (error) {
      toast.error(error.message || 'Failed to respond to transfer');
    } finally {
      setActionLoading(false);
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      let url = '/api/jobs/search?';
      if (location) {
        url += `lat=${location.lat}&lng=${location.lng}&radius_km=${filters.radius_km}`;
      }
      if (filters.category) url += `&category=${filters.category}`;
      if (filters.industry) url += `&industry=${filters.industry}`;
      if (filters.pay_min) url += `&pay_min=${filters.pay_min}`;
      
      const response = await apiCall(url);
      setJobs(response.jobs);
      setJobsMeta({ locationRequired: !!response.location_required, effectiveRadiusKm: response.effective_max_radius_km ?? null });
      if (response.platform_min_rate != null) setPlatformMinPay(Number(response.platform_min_rate));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadWallet = async () => {
    try {
      const response = await apiCall('/api/wallet');
      // Handle both cases: when wallet exists (response.wallet) and when it doesn't (response.balance)
      if (response.wallet) {
        setWallet(response.wallet);
      } else {
        setWallet({ balance: response.balance || 0, transactions: [] });
      }
      // Unified transaction history (Financial Engine source of truth)
      const txResp = await apiCall('/api/wallet/transactions');
      setWalletTxns(txResp.transactions || []);
    } catch (error) {
      console.error('Failed to load wallet:', error);
      setWallet({ balance: 0, transactions: [] });
    }
  };

  const loadEarnings = async () => {
    try {
      const response = await apiCall('/api/worker/earnings');
      setEarnings(response);
    } catch (error) {
      console.error('Failed to load earnings:', error);
      setEarnings({ total_earned: 0, pending_amount: 0, shifts_completed: 0 });
    }
  };

  const loadMinPayPrefs = async () => {
    try {
      const res = await apiCall('/api/worker/my-availability');
      const rate = res?.availability?.min_pay_rate;
      if (rate) setWorkerMinRate(Number(rate));
    } catch (_e) { /* non-blocking */ }
  };

  const isBelowWorkerMin = (job) =>
    !!workerMinRate &&
    (job.compensation_type || 'hourly') === 'hourly' &&
    Number(job.pay_rate || 0) > 0 &&
    Number(job.pay_rate || 0) < workerMinRate;

  const isLowPayJob = (job) => !!job.below_platform_min || isBelowWorkerMin(job);

  const visibleJobs = hideBelowMin ? jobs.filter((j) => !isLowPayJob(j)) : jobs;

  const handlePickJob = async (jobId, acknowledgeLowPay = false) => {
    try {
      const opts = { method: 'POST' };
      if (acknowledgeLowPay) opts.body = JSON.stringify({ acknowledge_low_pay: true });
      const res = await apiCall(`/api/jobs/${jobId}/pick`, opts);
      toast.success(res.message || 'Request sent — awaiting employer approval');
      setLowPayConfirmJob(null);
      loadJobs();
    } catch (error) {
      const msg = (error.message || '').trim();
      // Detect our machine-readable prefixes and show friendlier copy.
      if (msg.includes('LOW_PAY_ACK_REQUIRED')) {
        const job = jobs.find((j) => j._id === jobId);
        setLowPayConfirmJob(job || { _id: jobId });
        return;
      }
      const friendly = {
        SHIFT_UNAVAILABLE: 'This shift is no longer available.',
        EMPLOYER_UNVERIFIED: 'This employer has not yet completed verification. Please try again once they\'re approved.',
        DUP_APPLIED: 'You\'ve already applied to this shift.',
        PREVIOUSLY_REJECTED: 'You previously declined this shift and the employer\'s policy does not allow re-applying.',
        REAPPLY_TIMEOUT: 'You cannot re-apply yet — the employer\'s cooldown is still active.',
        ACCEPTANCE_WINDOW_CLOSED: 'This shift has already started and the acceptance grace period has passed.',
        SHIFT_ENDED: 'This shift has already ended.',
        MISSING_REQUIREMENTS: 'You do not currently meet the requirements for this shift. Please update your compliance status.',
        JOB_NOT_FOUND: 'This shift no longer exists.',
        INVALID_JOB_ID: 'Invalid shift id.',
      };
      const code = Object.keys(friendly).find((k) => msg.includes(k));
      if (code) {
        toast.error(friendly[code]);
        loadJobs();
        return;
      }
      toast.error(msg || 'Something went wrong while accepting this shift. Please try again.');
    }
  };

  const handleLowPayDecline = () => {
    const job = lowPayConfirmJob;
    setLowPayConfirmJob(null);
    if (job?._id) {
      apiCall('/api/worker/low-pay-decline', {
        method: 'POST',
        body: JSON.stringify({ job_id: job._id }),
      }).catch(() => {});
    }
  };

  const handleSaveJob = async (jobId) => {
    try {
      await apiCall(`/api/worker/saved-jobs/${jobId}`, { method: 'POST' });
      toast.success('Job saved!');
    } catch (error) {
      if (error.message?.includes('already saved')) {
        toast.info('Job already saved');
      } else {
        toast.error(error.message);
      }
    }
  };

  const handleCheckIn = async (jobId) => {
    if (!location) {
      toast.error('Location required for check-in');
      return;
    }
    try {
      await apiCall('/api/attendance/checkin', {
        method: 'POST',
        body: JSON.stringify({ job_id: jobId, lat: location.lat, lng: location.lng }),
      });
      toast.success('Checked in successfully!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCheckOut = async (jobId) => {
    if (!location) {
      toast.error('Location required for check-out');
      return;
    }
    try {
      const response = await apiCall('/api/attendance/checkout', {
        method: 'POST',
        body: JSON.stringify({ job_id: jobId, lat: location.lat, lng: location.lng }),
      });
      toast.success(`Checked out! Duration: ${response.duration_hours.toFixed(2)} hours`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const tabs = [
    { id: 'home', label: 'Home', icon: <Home className="w-5 h-5" /> },
    { id: 'messages', label: 'Messages', icon: <MessageSquare className="w-5 h-5" /> },
    { id: 'jobs', label: 'Find Jobs', icon: <Briefcase className="w-5 h-5" /> },
    { id: 'my-shifts', label: 'My Shifts', icon: <Calendar className="w-5 h-5" /> },
    { id: 'timesheets', label: 'Timesheets', icon: <ClockIcon className="w-5 h-5" /> },
    { id: 'payouts', label: 'Payouts', icon: <Banknote className="w-5 h-5" /> },
    { id: 'feed', label: 'Feed', icon: <Rss className="w-5 h-5" /> },
    { id: 'training', label: 'Training', icon: <GraduationCap className="w-5 h-5" /> },
    { id: 'shift-swap', label: 'Shift Swap', icon: <ArrowLeftRight className="w-5 h-5" /> },
    { id: 'journal', label: 'Journal', icon: <BookOpen className="w-5 h-5" /> },
    { id: 'leaderboard', label: 'Leaderboard', icon: <Trophy className="w-5 h-5" /> },
    { id: 'challenges', label: 'Challenges', icon: <Zap className="w-5 h-5" /> },
    { id: 'calendar', label: 'Calendar', icon: <Calendar className="w-5 h-5" /> },
    { id: 'marketplace', label: 'Marketplace', icon: <Store className="w-5 h-5" /> },
    { id: 'shift-history', label: 'History', icon: <History className="w-5 h-5" /> },
    { id: 'directory', label: 'Directory', icon: <Users2 className="w-5 h-5" /> },
    { id: 'availability', label: 'Availability', icon: <ClockIcon className="w-5 h-5" /> },
    { id: 'wallet', label: 'Wallet', icon: <Wallet className="w-5 h-5" /> },
    { id: 'forecast', label: 'Forecast', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'instant-pay', label: 'Instant Pay', icon: <Banknote className="w-5 h-5" /> },
    { id: 'referrals', label: 'Referrals', icon: <UserPlusIcon className="w-5 h-5" /> },
    { id: 'ai-match', label: 'AI Match', icon: <Sparkles className="w-5 h-5" /> },
    { id: 'surge', label: 'Surge', icon: <Flame className="w-5 h-5" /> },
    { id: 'heatmap', label: 'Heatmap', icon: <TrendingUp className="w-5 h-5" /> },
    { id: 'commute', label: 'Commute', icon: <Navigation className="w-5 h-5" /> },
    { id: 'wellness', label: 'Wellness', icon: <Heart className="w-5 h-5" /> },
    { id: 'profile', label: 'Profile', icon: <User className="w-5 h-5" /> },
    { id: 'hris', label: 'HR & Compliance', icon: <FileText className="w-5 h-5" /> },
  ];

  // Grouped nav — the menu bar collapses the 25+ tabs into 4 themed
  // dropdowns plus Home and Profile. Each group entry maps to an existing
  // tab id (preferred) or fires a custom action (e.g. shift group chat,
  // help & support).
  const navGroups = [
    {
      key: 'messages',
      label: 'Messages',
      icon: <MessageSquare className="w-4 h-4" />,
      items: [
        { id: 'messages', label: 'Messages', icon: <MessageSquare className="w-4 h-4" />, desc: 'Direct conversations' },
        { id: 'community', label: 'Announcements', icon: <Megaphone className="w-4 h-4" />, desc: 'From your organisation\'s HR' },
        { id: 'feed', label: 'Feed', icon: <Rss className="w-4 h-4" />, desc: 'Community posts' },
        { id: 'my-shifts', label: 'Shift Group Chat', icon: <Users2 className="w-4 h-4" />, desc: 'Chat per shift' },
        { id: 'directory', label: 'Directory', icon: <Users2 className="w-4 h-4" />, desc: 'Find a contact' },
      ],
    },
    {
      key: 'jobs',
      label: 'Jobs',
      icon: <Briefcase className="w-4 h-4" />,
      items: [
        { id: 'my-shifts', label: 'My Shifts', icon: <Calendar className="w-4 h-4" />, desc: 'Upcoming & active' },
        { id: 'jobs', label: 'Find Jobs', icon: <Briefcase className="w-4 h-4" />, desc: 'Browse openings' },
        { id: 'invites', label: 'Invitations', icon: <Mail className="w-4 h-4" />, desc: 'Employer invites — accept or decline' },
        { id: 'ai-match', label: 'AI Match', icon: <Sparkles className="w-4 h-4" />, desc: 'Smart recommendations' },
        { id: 'shift-swap', label: 'Shift Swap', icon: <ArrowLeftRight className="w-4 h-4" />, desc: 'Trade or claim' },
        { id: 'timesheets', label: 'Timesheets', icon: <ClockIcon className="w-4 h-4" />, desc: 'Logs & disputes' },
        { id: 'availability', label: 'Availability', icon: <ClockIcon className="w-4 h-4" />, desc: 'Set your hours' },
        { id: 'shift-history', label: 'History', icon: <History className="w-4 h-4" />, desc: 'Past shifts' },
      ],
    },
    {
      key: 'payments',
      label: 'Payments',
      icon: <Wallet className="w-4 h-4" />,
      items: [
        { id: 'wallet', label: 'Wallet', icon: <Wallet className="w-4 h-4" />, desc: 'Balance & history' },
        { id: 'payouts', label: 'Payouts', icon: <Banknote className="w-4 h-4" />, desc: 'Transfers' },
        { id: 'instant-pay', label: 'Instant Pay', icon: <Banknote className="w-4 h-4" />, desc: 'Tap to cash out' },
        { id: 'forecast', label: 'Forecast', icon: <BarChart3 className="w-4 h-4" />, desc: 'Future earnings' },
        { id: 'referrals', label: 'Referrals', icon: <UserPlusIcon className="w-4 h-4" />, desc: 'Invite & earn' },
        { id: 'marketplace', label: 'Marketplace', icon: <Store className="w-4 h-4" />, desc: 'Buy & sell' },
      ],
    },
    {
      key: 'resources',
      label: 'Resources',
      icon: <Sparkles className="w-4 h-4" />,
      items: [
        { id: 'leaderboard', label: 'Leaderboard', icon: <Trophy className="w-4 h-4" />, desc: 'Rankings' },
        { id: 'challenges', label: 'Challenges', icon: <Zap className="w-4 h-4" />, desc: 'Earn rewards' },
        { id: 'training', label: 'Training', icon: <GraduationCap className="w-4 h-4" />, desc: 'Modules & quizzes' },
        { id: 'journal', label: 'Journal', icon: <BookOpen className="w-4 h-4" />, desc: 'Notes & reflections' },
        { id: 'calendar', label: 'Calendar', icon: <Calendar className="w-4 h-4" />, desc: 'Day view' },
        { id: 'heatmap', label: 'Heatmap', icon: <TrendingUp className="w-4 h-4" />, desc: 'Hot zones' },
        { id: 'surge', label: 'Surge', icon: <Flame className="w-4 h-4" />, desc: 'Premium pay periods' },
        { id: 'commute', label: 'Commute', icon: <Navigation className="w-4 h-4" />, desc: 'Best route' },
        { id: 'wellness', label: 'Wellness', icon: <Heart className="w-4 h-4" />, desc: 'Mind & body' },
        { id: 'hris', label: 'HR & Compliance', icon: <FileText className="w-4 h-4" />, desc: 'Certifications & IDs' },
        { id: 'help-training', label: 'Help & Training', icon: <BookOpen className="w-4 h-4" />, desc: 'Manuals & PDFs' },
        { id: '__help', label: 'Help & Support', icon: <HelpCircle className="w-4 h-4" />, desc: 'Get help now', action: 'help' },
      ],
    },
  ];

  // Gradient color arrays for job cards
  const gradients = [
    'from-blue-600 to-cyan-500',
    'from-purple-600 to-pink-500',
    'from-orange-500 to-red-500',
    'from-emerald-500 to-teal-500',
    'from-violet-600 to-indigo-500',
    'from-amber-500 to-orange-500',
  ];

  // iter91: Extracted so the nested ternaries in the earning-tier /
  // shift-frequency arms read as a lookup instead of an expression jungle.
  const _earnedBonus = (total) => {
    if (total > 500) return 20;
    if (total > 100) return 10;
    return 0;
  };
  const _monthlyBonus = (shifts) => {
    if (shifts > 3) return 10;
    if (shifts > 0) return 5;
    return 0;
  };
  // Wellness score calculation (matches legacy worker app formula)
  const wellnessScore = Math.min(100, Math.round(
    ((earnings?.shifts_completed || 0) * 5) +
    _earnedBonus(earnings?.total_earned || 0) +
    ((earnings?.hours_worked || 0) > 0 ? 10 : 0) +
    _monthlyBonus(earnings?.monthly_shifts || 0) +
    40 // base score
  ));
  const [showWellnessDropdown, setShowWellnessDropdown] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* First-login training card — self-dismisses once seen */}
      <Suspense fallback={null}><OnboardingCard role="worker" /></Suspense>
      {/* Header - Redesigned to match original worker app */}
      {/* Sticky chrome: header + tab nav move together so nothing hides under a hard-coded offset */}
      <div className="sticky top-0 z-30" data-testid="worker-sticky-chrome">
      <header className="bg-white border-b border-gray-200 ">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-wrap justify-between items-center gap-y-2">
            {/* Logo */}
            <ScensobConnectLogo size={36} subtitle="Worker Portal" wordmarkColor="#0f172a" />

            {/* Right Side Controls */}
            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
              {/* Total Earned Badge */}
              <div className="hidden md:flex items-center gap-3">
                <div className="bg-emerald-50 px-4 py-1.5 rounded-lg border border-emerald-100">
                  <p className="text-[10px] text-emerald-600 uppercase font-semibold tracking-wide">Total Earned</p>
                  <p className="text-lg font-bold text-emerald-700 tabular-nums" data-testid="header-total-earned">
                    ${(earnings?.lifetime_earned ?? earnings?.total_earned)?.toFixed(2) || '0.00'}
                  </p>
                </div>
                
                {/* Pending Badge */}
                <div className="bg-amber-50 px-4 py-1.5 rounded-lg border border-amber-100">
                  <p className="text-[10px] text-amber-600 uppercase font-semibold tracking-wide">Pending</p>
                  <p className="text-lg font-bold text-amber-700 tabular-nums" data-testid="header-pending">
                    ${earnings?.pending_amount?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>

              {/* Notification Bell */}
              <NotificationBell onNavigate={(type) => {
                // Map notification types → worker tab ids so clicking a
                // notification jumps the user straight to the relevant info.
                const NAV_MAP = {
                  shift_cancelled: 'my-shifts', shift_postponed: 'my-shifts',
                  shift_unavailable: 'my-shifts', shift_reminder: 'my-shifts',
                  job_confirmed: 'my-shifts', shift_confirmed: 'my-shifts',
                  shift_approved: 'my-shifts', shift_rejected: 'my-shifts',
                  shift_request: 'my-shifts', confirm_reminder: 'my-shifts',
                  shift_filled: 'my-shifts',
                  job_posted: 'jobs', new_application: 'jobs',
                  shift_auto_reopened: 'jobs',
                  job_invite: 'invites',
                  invite_accepted: 'home', invite_declined: 'home',
                  payment_received: 'payouts', payment_approved: 'payouts',
                  new_message: 'messages', broadcast: 'messages',
                  direct_message: 'messages',
                  application_approved: 'my-shifts',
                  training_reminder: 'training',
                  marketplace_listing_approve: 'marketplace',
                  marketplace_listing_reject: 'marketplace',
                  marketplace_listing_archive: 'marketplace',
                  marketplace_featured: 'marketplace',
                  incident_report: 'home',
                  timesheet_acknowledged: 'timesheets',
                  timesheet_edited: 'timesheets',
                  timesheet_dispute: 'timesheets',
                  dispute_resolved: 'timesheets',
                  time_edit_request: 'timesheets',
                  time_edit_response: 'timesheets',
                  cert_pending_review: 'profile',
                  cert_verified: 'profile', cert_rejected: 'profile',
                  shift_swap_posted: 'shift-swap',
                  shift_swap_claimed: 'shift-swap',
                  shift_swap_claimed_yours: 'shift-swap',
                  shift_transfer_request: 'my-shifts',
                  shift_transfer_requested: 'my-shifts',
                  transfer_accepted: 'my-shifts',
                  transfer_rejected: 'my-shifts',
                  transfer_completed: 'my-shifts',
                  training_completed: 'training',
                  training_assigned: 'training',
                  training_due_soon: 'training',
                  training_certificate: 'training',
                  training_nudge: 'training',
                  emergency_sos: 'home',
                  worker_checked_in: 'home', worker_checked_out: 'home',
                  tasks_all_complete: 'home',
                  hris_expired: 'hris',
                  hris_expiring_7d: 'hris',
                  hris_expiring_30d: 'hris',
                };
                const tab = NAV_MAP[type];
                if (tab) setActiveTab(tab);
              }} />

              {/* Wellness Score Badge */}
              <div className="relative">
                <button
                  onClick={() => setShowWellnessDropdown(!showWellnessDropdown)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-full transition"
                  data-testid="wellness-badge"
                >
                  <Heart className="w-4 h-4 text-teal-600 fill-teal-500" />
                  <span className="text-sm font-bold text-teal-700">{wellnessScore}</span>
                </button>

                {/* Wellness Dropdown */}
                {showWellnessDropdown && (
                  <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-4 bg-gradient-to-br from-teal-500 to-emerald-500 text-white">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-lg">Wellness Dashboard</h3>
                        <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
                          {(() => {
                            if (wellnessScore >= 80) return 'Great Balance';
                            if (wellnessScore >= 50) return 'Good';
                            return 'Needs Work';
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                          <span className="text-2xl font-bold">{wellnessScore}</span>
                        </div>
                        <div className="space-y-1 text-sm text-white/90">
                          <p className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5" />
                            {(earnings?.hours_worked || 0).toFixed(0)}h this week
                          </p>
                          <p className="flex items-center gap-2">
                            <Zap className="w-3.5 h-3.5" />
                            {earnings?.shifts_completed || 0} shifts
                          </p>
                          <p className="flex items-center gap-2">
                            <Star className="w-3.5 h-3.5" />
                            {user?.consecutive_days || 0} consecutive days
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xl font-bold text-gray-900">{(earnings?.monthly_hours || 0).toFixed(0)}h</p>
                        <p className="text-xs text-gray-500">This month</p>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xl font-bold text-gray-900">{earnings?.monthly_shifts || 0}</p>
                        <p className="text-xs text-gray-500">Monthly shifts</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Messages Button — opens the same Messages experience as the taskbar tab */}
              <button
                onClick={() => setActiveTab('messages')}
                className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors relative"
                title="Messages"
                data-testid="open-messaging-button"
              >
                <MessageSquare className="w-5 h-5 text-gray-600" />
                {unreadMessages > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-testid="worker-header-unread-badge">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </button>

              {/* Job Alerts Icon - near wellness badge */}
              <div className="relative" ref={jobAlertsRef}>
                <button
                  onClick={() => setShowJobAlerts(!showJobAlerts)}
                  className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors relative"
                  title="Job Alerts"
                  data-testid="job-alerts-bell"
                >
                  <Bell className="w-5 h-5 text-gray-600" />
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">!</span>
                </button>
                {showJobAlerts && (
                  <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <JobAlerts
                      asDropdown={true}
                      onClose={() => setShowJobAlerts(false)}
                      onApplyFilter={(f) => {
                        // Reset pay/category/industry/skills, then apply the requested ones
                        setFilters((prev) => ({
                          ...prev,
                          category: f.category ?? '',
                          industry: f.industry ?? '',
                          pay_min: f.pay_min ?? '',
                          skills: f.search ?? '',
                        }));
                        setActiveTab('jobs');
                        setShowJobAlerts(false);
                      }}
                    />
                  </div>
                )}
              </div>
              
              {/* Profile Dropdown */}
              <RoleSwitcher className="hidden md:flex" />
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-xl transition-colors"
                  data-testid="profile-dropdown-btn"
                >
                  <div className="relative">
                    {(user?.profile_photo || user?.profile_image) ? (
                      <img 
                        src={getProfilePhotoUrl(user.profile_photo || user.profile_image)} 
                        alt={user?.full_name}
                        className="w-10 h-10 rounded-xl object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg border-2 border-gray-200">
                        {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                    {isConnected && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" title="Online" />
                    )}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-gray-900">{user?.full_name?.split(' ')[0]}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-50">
                    {/* Profile Header with Verified Badge */}
                    <div className="p-4 bg-gradient-to-br from-slate-800 to-slate-900">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {(user?.profile_photo || user?.profile_image) ? (
                            <img src={getProfilePhotoUrl(user.profile_photo || user.profile_image)} alt={user?.full_name} className="w-14 h-14 rounded-xl object-cover border-2 border-white/30" data-testid="worker-profile-avatar-large" />
                          ) : (
                            <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white font-bold text-xl border-2 border-white/30">
                              {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                          )}
                          {/* Camera overlay — click to upload */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); profilePhotoInputRef.current?.click(); }}
                            disabled={uploadingPhoto}
                            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white text-slate-900 border-2 border-white shadow-md flex items-center justify-center hover:scale-110 transition disabled:opacity-60"
                            title="Change profile photo"
                            data-testid="worker-profile-photo-change-btn"
                          >
                            {uploadingPhoto ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Camera className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <input
                            ref={profilePhotoInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleProfilePhotoUpload}
                            className="hidden"
                            data-testid="worker-profile-photo-input"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-white">{user?.full_name}</p>
                          <p className="text-sm text-white/70">{user?.email}</p>
                          <div className="mt-1.5">
                            <Suspense fallback={null}><TrainingStreakBadge variant="dark" showCertificates testId="worker-profile-streak-badge" /></Suspense>
                          </div>
                        </div>
                        <div className="px-2 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-lg">
                          <p className="text-[10px] text-emerald-400 font-semibold">Verified</p>
                          <p className="text-[10px] text-emerald-300">Profile</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-2">
                      <RoleSwitcher variant="menu" onDone={() => setShowProfileDropdown(false)} />
                      <button
                        onClick={() => { setActiveTab('profile'); setShowProfileDropdown(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-left"
                        data-testid="dropdown-profile-btn"
                      >
                        <User className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">My Profile</span>
                      </button>
                      <button
                        onClick={() => { setShowProfileSettings(true); setShowProfileDropdown(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-left"
                        data-testid="dropdown-profile-settings-btn"
                      >
                        <Settings className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Profile Settings</span>
                      </button>
                      <button
                        onClick={() => { setActiveTab('wallet'); setShowProfileDropdown(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-left"
                      >
                        <Wallet className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Wallet & Earnings</span>
                      </button>
                      <button 
                        onClick={() => { setShowSettingsModal(true); setShowProfileDropdown(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-left"
                        data-testid="dropdown-settings-btn"
                      >
                        <Settings className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Settings</span>
                      </button>
                      <button 
                        onClick={() => { setShowLiveChat(true); setShowProfileDropdown(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-left"
                        data-testid="dropdown-help-btn"
                      >
                        <HelpCircle className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Help & Support</span>
                      </button>
                    </div>
                    
                    <div className="p-2 border-t border-gray-200">
                      <button
                        onClick={() => { setShowProfileDropdown(false); logout(); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-50 transition-colors text-left"
                        data-testid="dropdown-logout-btn"
                      >
                        <LogOut className="w-5 h-5 text-red-500" />
                        <span className="text-sm font-medium text-red-600">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs — EXACTLY 2 rows. Each row scrolls horizontally if viewport is narrow. */}
      <div
        className="worker-nav-glass"
        data-testid="worker-tab-nav"
      >
        <div className="worker-nav-aurora" aria-hidden="true" />
        <div ref={navRef} className="relative container mx-auto px-4 py-2">
          <div className="flex items-center gap-1 flex-wrap worker-nav-row" data-testid="worker-nav-row">
            {/* Home — always primary, no dropdown */}
            <button
              onClick={() => { setActiveTab('home'); setOpenNavGroup(null); }}
              className={`group relative whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                activeTab === 'home'
                  ? 'text-white shadow-lg shadow-indigo-500/30'
                  : 'text-slate-600 hover:text-indigo-700 hover:bg-white/70'
              }`}
              data-testid="worker-tab-home"
              aria-current={activeTab === 'home' ? 'page' : undefined}
            >
              {activeTab === 'home' && <span className="nav-active-pill" aria-hidden="true" />}
              <span className="relative flex items-center gap-1.5">
                <Home className="w-4 h-4" />
                Home
              </span>
            </button>

            {/* Grouped dropdowns */}
            {navGroups.map((group) => {
              const groupActive = group.items.some((it) => it.id && activeTab === it.id);
              const open = openNavGroup === group.key;
              const showBadge = group.key === 'messages' && unreadMessages > 0;
              return (
                <div key={group.key} className="relative">
                  <button
                    onClick={() => setOpenNavGroup(open ? null : group.key)}
                    className={`group relative whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                      (groupActive || open)
                        ? 'text-white shadow-lg shadow-indigo-500/30'
                        : 'text-slate-600 hover:text-indigo-700 hover:bg-white/70'
                    }`}
                    data-testid={`worker-nav-group-${group.key}`}
                    aria-expanded={open}
                    aria-haspopup="menu"
                  >
                    {(groupActive || open) && <span className="nav-active-pill" aria-hidden="true" />}
                    <span className="relative flex items-center gap-1.5">
                      {group.icon}
                      {group.label}
                      {showBadge && (
                        <span className="bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] px-1 flex items-center justify-center" data-testid="worker-tab-unread-badge">
                          {unreadMessages > 9 ? '9+' : unreadMessages}
                        </span>
                      )}
                      <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </span>
                  </button>
                  {open && (
                    <div
                      className="absolute left-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 max-h-[70vh] overflow-y-auto"
                      role="menu"
                      data-testid={`worker-nav-group-${group.key}-menu`}
                    >
                      {group.items.map((it) => {
                        const isActive = it.id && activeTab === it.id;
                        return (
                          <button
                            key={`${group.key}-${it.id || it.label}`}
                            onClick={() => {
                              if (it.action === 'help') {
                                setShowLiveChat(true);
                              } else if (it.id) {
                                setActiveTab(it.id);
                              }
                              setOpenNavGroup(null);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition ${
                              isActive ? 'bg-indigo-50' : 'hover:bg-slate-50'
                            }`}
                            data-testid={`worker-nav-item-${group.key}-${it.id || 'action'}`}
                            role="menuitem"
                          >
                            <div className={`p-1.5 rounded-lg ${isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'}`}>
                              {it.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className={`text-sm font-semibold truncate ${isActive ? 'text-indigo-700' : 'text-slate-800'}`}>{it.label}</p>
                                {it.id === 'messages' && unreadMessages > 0 && (
                                  <span className="bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center">
                                    {unreadMessages > 9 ? '9+' : unreadMessages}
                                  </span>
                                )}
                              </div>
                              {it.desc && <p className="text-[11px] text-slate-500 truncate">{it.desc}</p>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Profile — always trailing */}
            <button
              onClick={() => { setActiveTab('profile'); setOpenNavGroup(null); }}
              className={`group relative whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ml-auto ${
                activeTab === 'profile'
                  ? 'text-white shadow-lg shadow-indigo-500/30'
                  : 'text-slate-600 hover:text-indigo-700 hover:bg-white/70'
              }`}
              data-testid="worker-tab-profile"
              aria-current={activeTab === 'profile' ? 'page' : undefined}
            >
              {activeTab === 'profile' && <span className="nav-active-pill" aria-hidden="true" />}
              <span className="relative flex items-center gap-1.5">
                <User className="w-4 h-4" />
                Profile
              </span>
            </button>
          </div>
        </div>
      </div>
      </div>{/* /sticky chrome */}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        <Suspense fallback={<div className="flex justify-center py-16"><span className="w-6 h-6 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin" /></div>}>
        {/* Jobs Tab */}
        <WorkerJobsTab ctx={{ AdvancedJobFilters, DistanceUnitToggle, activeTab, distanceUnit, earnings, filters, gradients, handleDisableLocation, handlePickJob, handleSaveJob, hideBelowMin, isBelowWorkerMin, isLowPayJob, jobs, jobsMeta, loadJobs, loadLocation, loading, location, platformMinPay, setDistanceUnit, setFilters, setHideBelowMin, setLowPayConfirmJob, setShowFilters, showFilters, visibleJobs, workerMinRate }} />

        {/* Low Pay Confirmation Modal */}
        {lowPayConfirmJob && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="low-pay-confirm-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-3 bg-amber-100 rounded-xl flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-slate-900">Low Pay Alert</h3>
                  <p className="text-sm text-slate-500 truncate">{lowPayConfirmJob.title}</p>
                </div>
              </div>
              <p className="text-sm text-slate-700 mb-2">
                This job pays <span className="font-bold">${lowPayConfirmJob.pay_rate}/hr</span>
                {lowPayConfirmJob.below_platform_min && platformMinPay ? (
                  <> — below the platform minimum of <span className="font-bold">${platformMinPay}/hr</span>.</>
                ) : isBelowWorkerMin(lowPayConfirmJob) ? (
                  <> — below your minimum rate preference of <span className="font-bold">${workerMinRate}/hr</span>.</>
                ) : '.'}
              </p>
              <p className="text-sm text-slate-600 mb-6">
                You can still accept this shift, but we want to make sure you're aware of the pay before proceeding.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleLowPayDecline}
                  className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition"
                  data-testid="low-pay-decline-btn"
                >
                  No, Skip This Job
                </button>
                <button
                  onClick={() => handlePickJob(lowPayConfirmJob._id, true)}
                  className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition"
                  data-testid="low-pay-accept-btn"
                >
                  Accept Anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {/* My Shifts Tab */}
        <WorkerMyShiftsTab ctx={{ GPSCheckInOut, ReliabilityBadge, WorkerPaymentClaims, actionLoading, activeTab, alertSetShifts, completedCount, filteredShifts, handleConfirmShift, handleRejectConfirmation, handleRespondToTransfer, handleSetJobAlert, inProgressShifts, jobs, loadAvailableWorkers, loadMyShifts, loadSwitchableShifts, loading, myShifts, openEditShiftModal, openRatingModal, pendingTransfers, ratedJobs, setActiveTab, setShiftFilter, setShowGroupChat, setShowRejectModal, setShowSwitchModal, setShowTransferModal, shiftFilter, upcomingCount }} />
        {/* Wallet Tab */}
        <WorkerWalletTab ctx={{ FinancialSummaryCard, FinancialTimeline, StatementDownload, TransactionsList, activeTab, wallet, walletTxns }} />

        {/* HR & Compliance tab (iter166) — sealed HRIS module */}
        {activeTab === 'hris' && <WorkerHrisPage />}

        {/* Profile Tab */}
        <WorkerProfileTab ctx={{ activeTab, earnings, getProfilePhotoUrl, setActiveTab, setShowCertificates, setShowEarnings, setShowProfileSettings, setShowWalletManagement, user }} />

        {/* Celebratory streak-reward banner — shows on every tab the moment a reward is released */}
        <Suspense fallback={null}>
          <StreakRewardBanner />
        </Suspense>

        {/* Messages / Inbox */}
        {activeTab === 'messages' && (
          <WorkerInbox currentUserId={user?._id} />
        )}

        {activeTab === 'home' && (
          <div className="space-y-4">
            {/* Pending invitations banner — only renders when there are 1+ invites */}
            <JobInvites variant="banner" />
            <DashboardHome 
              user={user}
              earnings={earnings}
              wallet={wallet}
              onNavigate={(tab) => setActiveTab(tab)}
            />
          </div>
        )}

        {/* Job Invites Tab */}
        {activeTab === 'invites' && (
          <JobInvites variant="full" />
        )}

        {/* Feed Tab */}
        {activeTab === 'feed' && (
          <WorkerFeed />
        )}

        {activeTab === 'community' && (
          <Suspense fallback={null}><CommunityBoard /></Suspense>
        )}

        {/* Training Tab */}
        {activeTab === 'training' && (
          <div className="space-y-8">
            <HRISWorkerTraining />
          </div>
        )}

        {/* Timesheets Tab */}
        {activeTab === 'timesheets' && (
          <WorkerTimesheets />
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <WorkerPayouts />
        )}

        {/* Shift Swap Tab */}
        {activeTab === 'shift-swap' && (
          <ShiftSwapMarketplace />
        )}

        {/* Journal Tab */}
        {activeTab === 'journal' && (
          <ShiftJournal />
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <WorkerLeaderboard />
        )}

        {/* Challenges Tab */}
        {activeTab === 'challenges' && (
          <div className="space-y-6">
            <DailySpinWheel onRewardEarned={() => loadEarnings()} />
            <WeeklyChallenges />
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <ShiftCalendar />
        )}

        {/* Marketplace Tab */}
        {activeTab === 'marketplace' && (
          <Marketplace onApply={() => loadJobs()} />
        )}

        {/* Shift History Tab */}
        {activeTab === 'shift-history' && (
          <ShiftHistory />
        )}

        {/* Directory Tab */}
        {activeTab === 'directory' && (
          <WorkerDirectory />
        )}

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <AvailabilityCalendarPro />
        )}

        {/* Earnings Forecaster Tab */}
        {activeTab === 'forecast' && (
          <EarningsForecaster />
        )}

        {/* Instant Pay Tab */}
        {activeTab === 'instant-pay' && (
          <InstantPay />
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <ReferralSystem />
        )}

        {/* AI Recommendations Tab */}
        {activeTab === 'ai-match' && (
          <AIRecommendations onApply={() => loadJobs()} />
        )}

        {/* Surge Shifts Tab */}
        {activeTab === 'surge' && (
          <SurgeShifts onApply={() => loadJobs()} />
        )}

        {/* Availability Heatmap Tab */}
        {activeTab === 'heatmap' && (
          <AvailabilityHeatmap />
        )}

        {/* Commute Planner Tab */}
        {activeTab === 'commute' && (
          <CommutePlanner />
        )}

        {/* Wellness Dashboard Tab */}
        {activeTab === 'wellness' && (
          <WellnessDashboard />
        )}

        {/* Help & Training Tab */}
        {activeTab === 'help-training' && (
          <HelpTraining role="worker" />
        )}
        </Suspense>
      </main>

      {/* Mobile Bottom Nav - Shows key tabs only */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 safe-area-pb">
        <div className="flex justify-around">
          {[
            { id: 'home', label: 'Home', icon: <Home className="w-5 h-5" /> },
            { id: 'jobs', label: 'Jobs', icon: <Briefcase className="w-5 h-5" /> },
            { id: 'my-shifts', label: 'Shifts', icon: <Calendar className="w-5 h-5" /> },
            { id: 'challenges', label: 'Rewards', icon: <Gift className="w-5 h-5" /> },
            { id: 'profile', label: 'Profile', icon: <User className="w-5 h-5" /> },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 flex flex-col items-center gap-1 transition-colors ${
                activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'
              }`}
              data-testid={`worker-mobile-tab-${tab.id}`}
            >
              {tab.icon}
              <span className="text-xs font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Messaging Inbox / Real-Time Chat — replaced by /messages tab using WorkerInbox */}

      {/* Profile Settings Modal */}
      {showProfileSettings && (
        <SensitiveGate title="Profile Settings" onCancel={() => setShowProfileSettings(false)}>
          <WorkerProfileSettings
            user={user}
            onClose={() => setShowProfileSettings(false)}
            onProfileUpdate={handleProfileUpdate}
          />
        </SensitiveGate>
      )}

      {/* Certificate Management Modal */}
      {showCertificates && (
        <CertificateManagement
          onClose={() => setShowCertificates(false)}
        />
      )}

      {/* Wallet Management Modal */}
      {showWalletManagement && (
        <SensitiveGate title="Wallet & Payments" onCancel={() => setShowWalletManagement(false)}>
          <WalletManagement
            onClose={() => setShowWalletManagement(false)}
          />
        </SensitiveGate>
      )}

      {/* Earnings Dashboard Modal */}
      {showEarnings && (
        <SensitiveGate title="Earnings" onCancel={() => setShowEarnings(false)}>
          <EarningsDashboard
            onClose={() => setShowEarnings(false)}
          />
        </SensitiveGate>
      )}

      {/* Job Center Modal */}
      {showJobCenter && (
        <JobCenter
          onClose={() => setShowJobCenter(false)}
        />
      )}

      {/* Shift Group Chat Modal */}
      {showGroupChat && (
        <ShiftGroupChat
          shift={showGroupChat}
          onClose={() => setShowGroupChat(null)}
        />
      )}

      <WorkerRatingModal ctx={{ handleSubmitRating, ratingData, ratingLoading, setRatingData, setShowRatingModal, showRatingModal }} />

      {/* Reject Shift Modal */}
      <WorkerRejectModal ctx={{ actionLoading, handleRejectShift, rejectReason, setRejectReason, setShowRejectModal, showRejectModal }} />

      {/* Transfer Shift Modal */}
      {editShiftTarget && (
        <Suspense fallback={null}>
          <TimeEditRequestModal
            attendanceStatus={{
              check_in_time: editShiftTarget.edit_status?.original_checkin_time,
              check_out_time: editShiftTarget.edit_status?.original_checkout_time,
              attendance_id: editShiftTarget.edit_status?.attendance_id,
            }}
            form={editShiftForm}
            onChange={handleEditShiftFormChange}
            loading={editShiftLoading}
            onSubmit={handleSubmitEditShift}
            onClose={() => setEditShiftTarget(null)}
          />
        </Suspense>
      )}

      {showLocationHelp && (
        <Suspense fallback={null}>
          <LocationHelpModal
            onClose={() => setShowLocationHelp(false)}
            onRetry={() => {
              setShowLocationHelp(false);
              locationDeniedRef.current = false;
              // Give the browser a beat to reflect the new permission
              // (user may have just flipped it in site settings).
              setTimeout(() => loadLocation({ forceFresh: true }), 250);
            }}
          />
        </Suspense>
      )}

      {showSwitchModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white flex items-center gap-3">
              <ArrowLeftRight className="w-7 h-7" />
              <div>
                <h3 className="text-lg font-bold">Switch Shift</h3>
                <p className="text-sm text-white/80">Move to another assigned shift without a payroll gap</p>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-gray-600 text-sm mb-4">
                Your current shift <span className="font-semibold">{showSwitchModal.title}</span> will auto clock-out at the same instant the new one clocks in.
              </p>
              {switchLoading && switchableShifts.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-sm">Loading eligible shifts…</div>
              ) : switchableShifts.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 rounded-xl text-slate-600" data-testid="switch-modal-empty">
                  <ArrowLeftRight className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  <p className="text-sm font-medium">No other confirmed shifts overlap right now.</p>
                  <p className="text-xs text-slate-500 mt-1">Ask HR or the employer to assign another shift, then try again.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {switchableShifts.map(s => (
                    <button
                      key={s._id}
                      type="button"
                      onClick={() => handleConfirmSwitch(s._id)}
                      disabled={switchLoading}
                      className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition disabled:opacity-50"
                      data-testid={`switch-target-${s._id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{s.title}</p>
                          <p className="text-xs text-gray-500 truncate">{s.employer_name}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {s.start_time ? new Date(s.start_time).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                            {s.end_time ? ' → ' + new Date(s.end_time).toLocaleString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <PayRate job={s} className="text-sm font-bold text-indigo-700 block" testId="upcoming-shift-rate" />
                          {s.confirmation_status === 'confirmed' && (
                            <p className="text-[10px] text-emerald-600">✓ Confirmed</p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => { setShowSwitchModal(null); setSwitchableShifts([]); }}
                className="flex-1 px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-xl transition"
                data-testid="switch-modal-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showTransferModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl flex-shrink-0">
              <div className="flex items-center gap-3 text-white">
                <UserPlus className="w-7 h-7" />
                <div>
                  <h3 className="text-lg font-bold">Transfer Shift</h3>
                  <p className="text-sm text-white/80">{showTransferModal.title}</p>
                </div>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-gray-600 mb-4">Select a worker to transfer this shift to. They will receive a request and can accept or decline.</p>
              
              {/* Worker Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Worker</label>
                {availableWorkers.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {availableWorkers.map(worker => (
                      <button
                        key={worker._id}
                        type="button"
                        onClick={() => setSelectedTransferWorker(worker)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition text-left ${
                          selectedTransferWorker?._id === worker._id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`select-worker-${worker._id}`}
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                          {worker.full_name?.charAt(0) || 'W'}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{worker.full_name}</p>
                          <p className="text-sm text-gray-500">{worker.email}</p>
                        </div>
                        {worker.rating > 0 && (
                          <div className="flex items-center gap-1 text-amber-500">
                            <Star className="w-4 h-4 fill-amber-500" />
                            <span className="text-sm font-medium">{worker.rating.toFixed(1)}</span>
                          </div>
                        )}
                        {selectedTransferWorker?._id === worker._id && (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-xl">
                    <User className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No other workers available for transfer</p>
                  </div>
                )}
              </div>

              {/* Transfer Reason */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Message to Worker (optional)</label>
                <textarea
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  placeholder="Let them know why you're transferring this shift..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-800 resize-none"
                  rows={2}
                  data-testid="transfer-reason-input"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setShowTransferModal(null);
                  setSelectedTransferWorker(null);
                  setTransferReason('');
                }}
                className="flex-1 px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                onClick={handleTransferShift}
                disabled={actionLoading || !selectedTransferWorker}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-transfer-shift-btn"
              >
                {actionLoading && <RefreshCw className="w-4 h-4 animate-spin" />}
                Send Transfer Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <Settings className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Settings</h3>
                  <p className="text-sm text-gray-500">Manage your preferences</p>
                </div>
              </div>
              <button onClick={() => setShowSettingsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Money notification preferences (functional) */}
              <NotificationPrefsCard role="worker" />

              {/* Notifications Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Bell className="w-4 h-4" /> Notifications
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Push Notifications</p>
                      <p className="text-xs text-gray-500">Get notified about new jobs and messages</p>
                    </div>
                    <input type="checkbox" defaultChecked className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500" />
                  </label>
                  <label className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Email Notifications</p>
                      <p className="text-xs text-gray-500">Receive job alerts via email</p>
                    </div>
                    <input type="checkbox" defaultChecked className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500" />
                  </label>
                  <label className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-700">SMS Notifications</p>
                      <p className="text-xs text-gray-500">Get text alerts for urgent shifts</p>
                    </div>
                    <input type="checkbox" className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500" />
                  </label>
                </div>
              </div>

              {/* Privacy Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Privacy
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Profile Visibility</p>
                      <p className="text-xs text-gray-500">Allow employers to view your profile</p>
                    </div>
                    <input type="checkbox" defaultChecked className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500" />
                  </label>
                  <label className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Location Sharing</p>
                      <p className="text-xs text-gray-500">Share location for nearby job matches</p>
                    </div>
                    <input type="checkbox" defaultChecked className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500" />
                  </label>
                </div>
              </div>

              {/* Language Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Language & Region
                </h4>
                <select className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="en">English (US)</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                </select>
              </div>
            </div>
            
            <div className="p-6 bg-gray-50 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="flex-1 px-4 py-3 text-gray-600 hover:bg-gray-200 rounded-xl transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => { toast.success('Settings saved!'); setShowSettingsModal(false); }}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Chat Support */}
      {showLiveChat && (
        <LiveChatSupport 
          isOpen={showLiveChat}
          onClose={() => setShowLiveChat(false)}
        />
      )}

      {/* Report Floating Button (formerly Emergency SOS) */}
      <ReportButton />
    </div>
  );
}
